import styled from '@emotion/styled'
import { Props as BenefitsProps } from 'app/components/Benefits'
import { Image } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { HeaderContext } from 'app/containers/Header'
import { styles } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo, useContext } from 'react'

import { Syncrobox, SyncroStatus } from './Syncrobox'

export interface Props {
  modalStatus?: boolean
  benefitsProps?: BenefitsProps
  syncroStatus?: SyncroStatus
}

export const ModalIBE = memo(function ModalIBE({
  modalStatus,
  benefitsProps,
  syncroStatus,
}: Props) {
  const { languageCode } = useContext(HeaderContext) as any

  return (
    <Container
      className={modalStatus ? 'open' : undefined}
      dial={4}
      onClick={(e) => e.stopPropagation()}
      row
      stretch
    >
      <LeftSide dial={2}>
        {modalStatus ? (
          <Image className="item-bg" src="/syncrobox.webp" />
        ) : null}

        {benefitsProps?.benefitsList && (
          <BenefitsList>
            <Label> {useVocabularyData('benefits-label', languageCode)}</Label>
            <Title> {useVocabularyData('benefits-title', languageCode)}</Title>
            {benefitsProps.benefitsList.map((benefit, index) => (
              <Row dial={4} row key={index}>
                <IconWrapper>
                  <Icon src={benefit.icon} alt={benefit.label} />
                </IconWrapper>
                {benefit.label}
              </Row>
            ))}
          </BenefitsList>
        )}
      </LeftSide>

      <RightSide dial={4}>
        <Label> {useVocabularyData('be-label', languageCode)}</Label>
        <Title> {useVocabularyData('be-title', languageCode)}</Title>

        <Syncrobox modalStatus={modalStatus} syncroStatus={syncroStatus} />
      </RightSide>
    </Container>
  )
})

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  -webkit-overflow-scrolling: auto;
  &.open {
    > div {
      transition: 0.6s ease-in-out;
      transform: none;
    }
  }

  > div {
    transition-delay: 0.6s;
  }
`

const LeftSide = styled(FlexBox)`
  width: 50%;
  background: ${({ theme }) => theme.colors.variants.neutralDark2};
  transform: translateX(-100%);
  padding: 10rem 0 0;

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark3};
    opacity: 0.8;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .item-bg img {
    position: absolute !important;
    ${styles.img}
  }

  @media (max-width: 1474px) {
    padding: 13.333vh 0 0;
  }
  @media (max-width: 1300px) {
    padding: 5rem 0 0;
  }
  @media (max-height: 719px) {
    padding: 5rem 0 0;
  }
`

const BenefitsList = styled.div`
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  padding: 0 11.111vw;

  @media (max-width: 1474px) {
    padding: 0 5rem 0 7.5rem;
  }
  @media (max-height: 719px) {
    padding: 0 5rem 0 7.5rem;
  }
`

const Row = styled(FlexBox)`
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  margin-top: 1.25rem;
  font-size: 1rem;
  line-height: 1.375rem;
  font-weight: 300;

  @media (max-width: 1474px) {
    margin-top: 1rem;
  }
  @media (max-height: 719px) {
    margin-top: 1rem;
  }
`

const IconWrapper = styled.div`
  background: ${({ theme }) => theme.colors.variants.neutralDark3};
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  position: relative;
  margin-right: 0.875rem;
`

const Icon = styled.img`
  width: 1rem;
  max-width: 1rem;
  height: auto;
  max-height: 1rem;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const RightSide = styled(FlexBox)`
  width: 50%;
  background: ${({ theme }) => theme.colors.variants.neutralDark3};
  transform: translateX(100%);
  padding: 10rem 11.111vw 0;

  @media (max-width: 1474px) {
    padding: 13.333vh 7.5rem 0;
  }
  @media (max-width: 1300px) {
    padding: 5rem 7.5rem 0;
  }
  @media (max-height: 719px) {
    padding: 5rem 7.5rem 0;
  }

  @media (max-width: 1199px) {
    display: block;
    width: 100%;
    transform: none;
  }
`

const Label = styled.div`
  ${styles.labelTitle}

  color: ${({ theme }) => theme.colors.variants.neutralLight4};

  @media (max-width: 1474px) {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
`

const Title = styled.div`
  ${styles.title.small}

  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  text-transform: uppercase;
  margin-top: 0.75rem;
  margin-bottom: 3rem;

  @media (max-width: 1474px) {
    margin-bottom: 1.875rem;
    font-size: 2.625rem;
    line-height: 2.625rem;
  }
`
